import { markRaw, type Ref } from 'vue'
import { aiChatApi, formatSseStreamError } from '@/api/aiChat'
import type { AiChatResponse, AiUserQuestionOptionView } from '@/api/aiChat'
import type { useAiPlanStore, AiPlanSnapshotEvent } from '@/stores/aiPlan'
import type { useAiSessionsStore } from '@/stores/aiSessions'
import { validateSseV2Envelope } from '@/types/aiSseV2Validator'
import { resolveAssistantVisibleText } from '@/utils/aiMessageDisplay'
import { pushAiDebug } from '@/utils/aiDebugLog'
import { logger } from '@/utils/logger'
import { coalesceServerMessagesForUi, isInternalRepairPayload } from './messages'
import {
  appendContentTimeline,
  appendReasoningTimeline,
  buildCompletedTurnView,
  mergeToolDoneIntoTimeline,
  pushToolPlaceholder,
  removeToolCallFromTimeline,
  syncFinalAssistantText
} from './timeline'
import { enqueueUserAction } from './userActionQueue'
import { sessionHasOpenUserAction } from './sessionTask'
import { isNonExecutedConfirmationOutcome, hasExplicitBusinessFailure } from './detectors'
import { isImportProgressComplete } from '@/utils/assistantTrailingHint'
import type { CancellableAbort, StreamRegistry } from './context'
import type {
  AiTimelineSegment,
  AiToolCallUi,
  AiToolCallView,
  AiUiMessage,
  ChatStreamAccumulator,
  PendingUserQuestion,
  ResumableStop,
  ResumableStopReason,
  UserActionItem
} from './types'
import { isResumableStopReason } from './types'

interface V2Envelope {
  event: string
  seq: number
  runId: string
  ts: number
  data: Record<string, unknown>
}

export interface ChatStreamBridge {
  pendingMsgId: string
  pendingMsgTemplate?: AiUiMessage
  messages: Ref<AiUiMessage[]>
  sessionId: Ref<string | null>
  userActionQueue: Ref<UserActionItem[]>
  lastError: Ref<string | null>
  /** 继续回合状态：run_done/run_error 终态判定后置位（驱动异常停止提示条） */
  resumableStop: Ref<ResumableStop | null>
  sessionsStore: ReturnType<typeof useAiSessionsStore>
  planStore?: ReturnType<typeof useAiPlanStore>
  hadPersistedSession: boolean
  abortCtrl: CancellableAbort
  acc: ChatStreamAccumulator
  clientUserMsgId?: string | null
  messagesBySession?: Map<string, AiUiMessage[]>
  /** 按会话分桶的在途流注册表：run_start 重定向归属会话时迁移登记 key。 */
  streamsBySession?: StreamRegistry
}

function ownerSessionIdOf(bridge: ChatStreamBridge): string {
  return (bridge.acc.ownerSessionId ?? bridge.sessionId.value ?? '').trim()
}

function isViewingOwner(bridge: ChatStreamBridge): boolean {
  const owner = ownerSessionIdOf(bridge)
  if (!owner) {
    return true
  }
  return bridge.sessionId.value?.trim() === owner
}

/**
 * §3.5-C：run_done 终态为 BATCH_WAITING 时，从服务端权威快照重新同步当前会话的待办队列。
 * 与 useSessionLifecycle.restoreV2PendingActions 同源逻辑：先按 session 清空、再按 pendingId
 * 去重入队。仅在计划卡分批确认（finish=BATCH_WAITING，同计划仍有待确认卡）场景下调用，
 * 避免每次 run_done 重置提问卡进度。覆盖断连重连后补看同计划下一张待确认卡（验收 10）。
 */
async function resyncPendingActionsAfterBatchWait(bridge: ChatStreamBridge): Promise<void> {
  const sid = ownerSessionIdOf(bridge)
  if (!sid) {
    return
  }
  try {
    const response = await aiChatApi.getV2PendingActions(sid)
    const actions = (response.data?.pendingActions ?? []) as unknown as Array<Record<string, unknown>>
    bridge.userActionQueue.value = bridge.userActionQueue.value.filter(
      (item) => item.conversationSessionId !== sid
    )
    for (const action of actions) {
      const pendingId = String(action.pendingId ?? '').trim()
      if (!pendingId) {
        continue
      }
      const kind = action.kind
      if (kind === 'QUESTION') {
        enqueueUserAction(bridge.userActionQueue, {
          type: 'question',
          id: pendingId,
          toolCallId: typeof action.toolCallId === 'string' ? action.toolCallId : undefined,
          conversationSessionId: sid,
          question: typeof action.prompt === 'string' ? action.prompt : '请补充信息',
          options: Array.isArray(action.options) ? (action.options as AiUserQuestionOptionView[]) : [],
          customOption: { id: 'custom', label: '自定义', maxLength: 200, placeholder: null },
          questionGroupId: typeof action.questionGroupId === 'string' ? action.questionGroupId : null,
          status: 'PENDING',
          expiresAt: typeof action.expiresAt === 'string' ? action.expiresAt : undefined,
          error: null
        })
        continue
      }
      enqueueUserAction(bridge.userActionQueue, {
        type: 'confirmation',
        id: pendingId,
        toolCallId: typeof action.toolCallId === 'string' ? action.toolCallId : undefined,
        conversationSessionId: sid,
        message: typeof action.displayLabel === 'string' ? `请确认：${action.displayLabel}` : '该操作需要你的确认',
        toolName: typeof action.toolName === 'string' ? action.toolName : undefined,
        displayLabel: typeof action.displayLabel === 'string' ? action.displayLabel : undefined,
        riskLevel: typeof action.riskLevel === 'string' ? action.riskLevel : undefined,
        effectiveRisk: typeof action.effectiveRisk === 'string' ? action.effectiveRisk : undefined,
        argumentsJson: typeof action.argumentsJson === 'string' ? action.argumentsJson : undefined,
        preview: action.preflightReport && typeof action.preflightReport === 'object'
          ? (action.preflightReport as Record<string, unknown>)
          : undefined,
        preflightReport: action.preflightReport && typeof action.preflightReport === 'object'
          ? (action.preflightReport as Record<string, unknown>)
          : undefined,
        requiresRiskAcknowledgement: action.requiresRiskAcknowledgement === true,
        expiresAt: typeof action.expiresAt === 'string' ? action.expiresAt : undefined,
        status: 'PENDING',
        error: null
      })
    }
  } catch {
    // 待办恢复临时不可用时，消息历史仍可用，不阻断主流程。
  }
}

function ownerMessageList(bridge: ChatStreamBridge): AiUiMessage[] {
  if (isViewingOwner(bridge) || !bridge.messagesBySession) {
    return bridge.messages.value
  }
  const owner = ownerSessionIdOf(bridge)
  return bridge.messagesBySession.get(owner) ?? []
}

function mutateOwnerMessages(
  bridge: ChatStreamBridge,
  mutator: (list: AiUiMessage[]) => void
): void {
  if (isViewingOwner(bridge) || !bridge.messagesBySession) {
    mutator(bridge.messages.value)
    return
  }
  const owner = ownerSessionIdOf(bridge)
  const cached = [...(bridge.messagesBySession.get(owner) ?? [])]
  mutator(cached)
  bridge.messagesBySession.set(owner, cached)
}

/**
 * 仅当用户仍停留在该流的归属会话上时，把视图会话绑定到服务端会话 id。
 * 用户已切走（current ≠ sid，包括切到空白新对话）时不抢视图。
 * 返回是否处于"停留"状态，供 run_start 的 activate 语义复用。
 */
function bindViewToOwnerIfNeeded(bridge: ChatStreamBridge, sid: string): boolean {
  const current = bridge.sessionId.value?.trim() ?? ''
  if (current !== sid) {
    return false
  }
  bridge.sessionId.value = sid
  return true
}

function scheduleCompletionReconcile(bridge: ChatStreamBridge): void {
  const targetSessionId = ownerSessionIdOf(bridge)
  if (!targetSessionId || typeof window === 'undefined') return
  if (!isViewingOwner(bridge)) return
  const expectedMessageCount = bridge.messages.value.length
  const delays = [250, 500, 900, 1400, 2000]

  const attempt = (index: number): void => {
    window.setTimeout(() => {
      void (async () => {
        if (bridge.sessionId.value?.trim() !== targetSessionId) return
        if (bridge.messages.value.length !== expectedMessageCount) return
        try {
          const response = await aiChatApi.listMessages(targetSessionId)
          const rows = response.data ?? []
          const lastAssistant = [...rows].reverse().find((row) => row.role === 'assistant')
          if (!lastAssistant || lastAssistant.status === 'ROUND_REPLY') {
            if (index + 1 < delays.length) attempt(index + 1)
            return
          }
          if (bridge.sessionId.value?.trim() !== targetSessionId) return
          if (bridge.messages.value.length !== expectedMessageCount) return
          bridge.messages.value = coalesceServerMessagesForUi(rows)
        } catch {
          if (index + 1 < delays.length) attempt(index + 1)
        }
      })()
    }, delays[index])
  }
  attempt(0)
}

function isEnvelope(value: unknown): value is V2Envelope {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<V2Envelope>
  return typeof candidate.event === 'string'
    && typeof candidate.seq === 'number'
    && typeof candidate.runId === 'string'
    && typeof candidate.ts === 'number'
    && Boolean(candidate.data && typeof candidate.data === 'object')
}

/** Reduces the single V2 SSE protocol into the chat timeline and pending-action queue. */
export function dispatchChatStreamEvent(
  wireEventName: string,
  rawData: unknown,
  bridge: ChatStreamBridge
): void {
  if (!isEnvelope(rawData)) {
    logger.error('[AI SSE v2] rejected non-envelope event', { wireEventName, rawData })
    pushAiDebug('stream', 'SSE v2 event missing transport envelope', { wireEventName })
    return
  }
  const validation = validateSseV2Envelope(rawData)
  if (!validation.ok) {
    logger.error('[AI SSE v2] contract violation', {
      wireEventName,
      rawData,
      errors: validation.errors
    })
    pushAiDebug('stream', 'SSE v2 contract violation', {
      wireEventName,
      errors: validation.errors?.slice(0, 3)
    })
    return
  }

  const envelope = rawData
  const eventName = envelope.event
  const data = envelope.data
  const {
    pendingMsgId,
    sessionId,
    userActionQueue,
    lastError,
    sessionsStore,
    hadPersistedSession,
    abortCtrl,
    acc
  } = bridge

  acc.v2RunId = envelope.runId
  abortCtrl._runId = envelope.runId
  if (envelope.seq <= (acc.v2LastSeq ?? 0)) return
  acc.v2LastSeq = envelope.seq

  if (wireEventName && wireEventName !== eventName) {
    pushAiDebug('stream', 'SSE wire name differs from envelope event', {
      wireEventName,
      envelopeEvent: eventName
    })
  }

  function currentAssistant(): AiUiMessage | null {
    return ownerMessageList(bridge).find((message) => message.id === pendingMsgId) ?? null
  }

  function patchPending(patch: Partial<AiUiMessage>): void {
    mutateOwnerMessages(bridge, (list) => {
      let index = list.findIndex((message) => message.id === pendingMsgId)
      if (index < 0) {
        for (let i = list.length - 1; i >= 0; i -= 1) {
          if (list[i].role === 'assistant' && list[i].pending) {
            index = i
            break
          }
        }
      }
      if (index >= 0) {
        // 性能债修复(热点2)：新时间线标记为原始数据，跳过 Vue 深度代理——
        // 片段总是整体替换、无细粒度响应式依赖；否则每 token 都付出
        // O(片段数)的深层代理重建成本（长轮次内二次方增长）。
        if (patch.timeline) markRaw(patch.timeline)
        Object.assign(list[index], patch)
      }
    })
  }

  if (eventName === 'heartbeat') return

  if (eventName === 'run_start') {
    const sid = typeof data.sessionId === 'string' ? data.sessionId.trim() : ''
    if (sid) {
      // 服务端重定向归属会话时，同步迁移注册表登记，保证收尾清理与 has() 查询一致。
      if (bridge.streamsBySession && acc.registeredSessionId && acc.registeredSessionId !== sid) {
        bridge.streamsBySession.move(acc.registeredSessionId, sid)
      }
      acc.registeredSessionId = sid
      acc.ownerSessionId = sid
      const attached = bindViewToOwnerIfNeeded(bridge, sid)
      const activate = attached
      if (!hadPersistedSession) {
        sessionsStore.registerServerSession(sid, null, { titlePending: true, activate })
      }
      sessionsStore.markRunning(sid)
    }
    return
  }

  if (eventName === 'reasoning_delta' || eventName === 'content_delta') {
    const text = typeof data.text === 'string' ? data.text : ''
    const previous = currentAssistant()
    patchPending({
      timeline: eventName === 'reasoning_delta'
        ? appendReasoningTimeline(previous?.timeline ?? undefined, text)
        : appendContentTimeline(previous?.timeline ?? undefined, text),
      awaitingNext: false,
      // 压缩完成后的下一轮流式输出必须结束「正在整理较早的对话记忆…」占位：
      // compressing 在 trailingHint 中优先级最高，不在此复位会一直粘滞到 run_done。
      compressing: false
    })
    return
  }

  if (eventName === 'tool_start') {
    const callId = typeof data.callId === 'string' ? data.callId : ''
    if (data.deferTimelineUntilResult === true) {
      patchPending({ awaitingNext: false })
      return
    }
    const previous = currentAssistant()
    const duplicate = (previous?.timeline ?? []).some(
      (segment) => segment.kind === 'tool' && segment.call.callId === callId
    )
    if (!callId || duplicate) {
      patchPending({ awaitingNext: false, compressing: false })
      return
    }
    const running: AiToolCallUi = {
      callId,
      toolName: typeof data.toolName === 'string' ? data.toolName : '',
      displayLabel: typeof data.displayLabel === 'string' ? data.displayLabel : null,
      argumentsJson: typeof data.argumentsJson === 'string'
        ? data.argumentsJson
        : typeof data.argsJson === 'string' ? data.argsJson : '{}',
      success: false,
      resultJson: null,
      errorMessage: null,
      durationMs: 0,
      pending: true,
      startedAt: Date.now()
    }
    patchPending({
      timeline: pushToolPlaceholder(previous?.timeline ?? undefined, running),
      awaitingNext: false,
      compressing: false
    })
    return
  }

  if (eventName === 'tool_done') {
    const resultJson = typeof data.resultJson === 'string'
      ? data.resultJson
      : data.resultView == null ? null : JSON.stringify(data.resultView)
    const toolCall: AiToolCallView = {
      callId: typeof data.callId === 'string' ? data.callId : '',
      toolName: typeof data.toolName === 'string' ? data.toolName : '',
      displayLabel: typeof data.displayLabel === 'string' ? data.displayLabel : null,
      argumentsJson: typeof data.argumentsJson === 'string' ? data.argumentsJson : '{}',
      success: data.success === true && !hasExplicitBusinessFailure(resultJson),
      resultJson,
      errorMessage: typeof data.errorMessage === 'string' ? data.errorMessage : null,
      durationMs: typeof data.durationMs === 'number' ? data.durationMs : 0,
      displayDisposition: data.displayDisposition === 'INTERNAL_REPAIR'
        ? 'INTERNAL_REPAIR'
        : 'VISIBLE',
      status: typeof data.status === 'string' ? data.status : undefined,
      capabilityKind: typeof data.capabilityKind === 'string' ? data.capabilityKind : undefined
    }
    const previous = currentAssistant()
    const internalRepair = data.displayDisposition === 'INTERNAL_REPAIR'
      || isInternalRepairPayload(toolCall.resultJson, toolCall.errorMessage ?? null)
    const didNotExecute = isNonExecutedConfirmationOutcome(toolCall)
    if (internalRepair || didNotExecute) {
      patchPending({
        timeline: removeToolCallFromTimeline(previous?.timeline ?? undefined, toolCall.callId),
        awaitingNext: true,
        importProgress: null
      })
      return
    }
    patchPending({
      timeline: mergeToolDoneIntoTimeline(previous?.timeline ?? undefined, toolCall),
      awaitingNext: true,
      importProgress: null
    })
    return
  }

  if (eventName === 'pending_question') {
    const pendingId = String(data.pendingId ?? data.pendingQuestionId ?? '').trim()
    const sid = ownerSessionIdOf(bridge)
    if (!pendingId || !sid) return
    const options = Array.isArray(data.options)
      ? data.options as AiUserQuestionOptionView[]
      : []
    const custom = data.customOption && typeof data.customOption === 'object'
      ? data.customOption as Record<string, unknown>
      : {}
    const question: PendingUserQuestion = {
      id: pendingId,
      toolCallId: typeof data.toolCallId === 'string' ? data.toolCallId : undefined,
      conversationSessionId: sid,
      question: String(data.prompt ?? data.question ?? '请补充信息'),
      options,
      customOption: {
        id: typeof custom.id === 'string' ? custom.id : 'custom',
        label: typeof custom.label === 'string' ? custom.label : '自定义',
        maxLength: typeof custom.maxLength === 'number' ? custom.maxLength : 200,
        placeholder: typeof custom.placeholder === 'string' ? custom.placeholder : null
      },
      toolName: typeof data.toolName === 'string' ? data.toolName : 'ask_user_question',
      status: 'PENDING',
      expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : undefined,
      error: null,
      questionGroupId: typeof data.groupId === 'string'
        ? data.groupId
        : typeof data.questionGroupId === 'string' ? data.questionGroupId : null,
      questionIndex: typeof data.number === 'number'
        ? data.number
        : typeof data.questionIndex === 'number' ? data.questionIndex : undefined,
      questionTotal: typeof data.total === 'number'
        ? data.total
        : typeof data.questionTotal === 'number' ? data.questionTotal : undefined
    }
    const index = userActionQueue.value.findIndex(
      (item) => item.type === 'question' && item.id === pendingId
    )
    if (index >= 0) {
      const next = [...userActionQueue.value]
      next[index] = { type: 'question', ...question }
      userActionQueue.value = next
    } else {
      userActionQueue.value = [...userActionQueue.value, { type: 'question', ...question }]
    }
    patchPending({
      timeline: removeToolCallFromTimeline(currentAssistant()?.timeline ?? undefined, question.toolCallId),
      awaitingNext: false,
      importProgress: null
    })
    return
  }

  if (eventName === 'pending_confirmation') {
    const pendingId = String(data.pendingId ?? data.pendingConfirmationId ?? '').trim()
    const sid = ownerSessionIdOf(bridge)
    if (!pendingId || !sid) return
    const toolCallId = typeof data.toolCallId === 'string' ? data.toolCallId : undefined
    enqueueUserAction(userActionQueue, {
      type: 'confirmation',
      id: pendingId,
      toolCallId,
      conversationSessionId: sid,
      message: String(data.message ?? data.summary ?? '该操作需要你确认后才会执行。'),
      toolName: typeof data.toolName === 'string' ? data.toolName : undefined,
      displayLabel: typeof data.displayLabel === 'string' ? data.displayLabel : undefined,
      riskLevel: typeof data.riskLevel === 'string' ? data.riskLevel : undefined,
      effectiveRisk: typeof data.effectiveRisk === 'string' ? data.effectiveRisk : undefined,
      argumentsJson: typeof data.argumentsJson === 'string' ? data.argumentsJson : undefined,
      preview: data.preview && typeof data.preview === 'object'
        ? data.preview as Record<string, unknown>
        : undefined,
      preflightReport: data.preflightReport && typeof data.preflightReport === 'object'
        ? data.preflightReport as Record<string, unknown>
        : undefined,
      impactDescription: typeof data.impactDescription === 'string'
        ? data.impactDescription
        : undefined,
      expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : undefined,
      requiresRiskAcknowledgement: data.requiresRiskAcknowledgement === true,
      status: 'PENDING',
      error: null
    })
    patchPending({
      timeline: removeToolCallFromTimeline(currentAssistant()?.timeline ?? undefined, toolCallId),
      awaitingNext: false,
      importProgress: null
    })
    return
  }

  if (eventName === 'pending_resolved') {
    const pendingId = typeof data.pendingId === 'string' ? data.pendingId.trim() : ''
    if (pendingId) {
      userActionQueue.value = userActionQueue.value.filter((item) => item.id !== pendingId)
    }
    patchPending({ awaitingNext: false })
    const sid = ownerSessionIdOf(bridge)
    const stillLive = Boolean(
      sid &&
      bridge.streamsBySession?.has(sid) &&
      !bridge.abortCtrl.signal.aborted
    )
    if (sid && !stillLive && !sessionHasOpenUserAction(sid, userActionQueue.value)) {
      sessionsStore.markEnded(sid)
    }
    return
  }

  if (eventName.startsWith('material.')) {
    const text = typeof data.text === 'string' ? data.text.trim() : ''
    const previous = currentAssistant()
    const nextTimeline = (previous?.timeline ?? []).map((segment) => {
      if (segment.kind !== 'tool' || !segment.call.pending) {
        return segment
      }
      const name = segment.call.toolName
      const matches = name === 'material_explore'
        || name === 'inspect_material'
        || name === 'read_range'
        || name === 'read_text'
        || name === 'search_material'
        || name === 'render_material'
        || name === 'run_python'
      if (!matches) {
        return segment
      }
      return { kind: 'tool' as const, call: { ...segment.call, progressHint: text || segment.call.progressHint } }
    })
    patchPending({
      timeline: nextTimeline,
      awaitingNext: false
    })
    return
  }

  if (eventName === 'import_progress') {
    if (isImportProgressComplete(data.progressPercent)) {
      patchPending({
        importProgress: null,
        awaitingNext: true,
        compressing: false
      })
      return
    }
    patchPending({
      importProgress: {
        stage: typeof data.stage === 'string' ? data.stage : 'EXTRACT',
        kind: typeof data.kind === 'string' ? data.kind : null
      },
      awaitingNext: false,
      compressing: false
    })
    return
  }

  if (eventName === 'compression_started') {
    // 后端上下文达 85% 触发的一次性总结调用（数秒暂停，AgentLoop 每轮 AI 思考前检查）。
    // 置位后底部显示「正在整理较早的对话记忆…」，在 trailingHint 中优先级最高；
    // 复位由压缩完成后的下一轮 *_delta / tool_start 事件完成。
    patchPending({ compressing: true })
    return
  }

  if (eventName === 'plan_snapshot') {
    const sid = ownerSessionIdOf(bridge)
    if (sid && bridge.planStore) {
      bridge.planStore.applySsePayload({ ...(data as AiPlanSnapshotEvent), sessionId: sid })
    }
    return
  }

  if (eventName === 'title') {
    const title = typeof data.title === 'string' ? data.title.trim() : ''
    const sid = ownerSessionIdOf(bridge)
    if (sid && title) {
      sessionsStore.registerServerSession(sid, title, {
        activate: isViewingOwner(bridge)
      })
    }
    return
  }

  if (eventName === 'run_done') {
    const tokenData = data.tokens && typeof data.tokens === 'object'
      ? data.tokens as Record<string, unknown>
      : {}
    const finishReason = typeof data.finish === 'string' ? data.finish : 'COMPLETED'
    const assistantMessage = typeof data.assistantMessage === 'string' ? data.assistantMessage : ''
    // COMPLETED 终态由服务端下发的任务耗时（与终行 taskDurationMs 同源），驱动实时折叠条。
    // wire 约定：元数据（rounds/reason/durationMs）随 tokens 对象下发（emitRunDone.usage 形参）。
    const serverDurationMs = typeof tokenData.durationMs === 'number' ? tokenData.durationMs : null
    const response: AiChatResponse = {
      sessionId: ownerSessionIdOf(bridge) || sessionId.value || '',
      conversationId: 0,
      assistantMessage,
      reasoningContent: null,
      toolCalls: [],
      finishReason,
      promptTokens: typeof tokenData.promptTokens === 'number' ? tokenData.promptTokens : 0,
      completionTokens: typeof tokenData.completionTokens === 'number' ? tokenData.completionTokens : 0,
      totalTokens: typeof tokenData.totalTokens === 'number' ? tokenData.totalTokens : 0,
      durationMs: serverDurationMs
    }
    acc.donePayload = response
    acc.doneProcessed = true
    if (!hadPersistedSession) void sessionsStore.loadFromServer()

    const previous = currentAssistant()
    if (previous) {
      const visibleReply = resolveAssistantVisibleText(assistantMessage)
      // 折叠仅在有权威终稿时进行：空终稿（最后一轮只输出推理/空文本）退回原展示，
      // 保留流式过程内容，避免清空气泡（与后端 persistCompletedTurn 的 isBlank 守卫同口径）。
      if (finishReason === 'COMPLETED' && visibleReply.trim().length > 0) {
        // 正常收尾：立即折叠（不等 reconcile 回填）。最后一个工具段之后的推理/正文
        // 属于模型最后一轮回复，作为消息主体；其余过程原样进折叠抽屉。正文以服务端
        // 权威终稿为准，与刷新后的服务端合并视图（messages.ts）同一契约。
        const view = buildCompletedTurnView(previous.timeline ?? undefined, assistantMessage, serverDurationMs)
        patchPending({
          content: view.content || previous.content,
          timeline: view.timeline,
          collapsedPriorRounds: view.collapsedPriorRounds,
          runStatus: view.collapsedPriorRounds ? 'normal' : null,
          pending: false,
          awaitingNext: false,
          error: null,
          importProgress: null
        })
      } else {
        const timeline = syncFinalAssistantText(previous.timeline ?? undefined, visibleReply)
        patchPending({
          content: visibleReply || previous.content,
          timeline: timeline.length > 0 ? timeline : null,
          pending: false,
          awaitingNext: false,
          error: null,
          importProgress: null
        })
      }
    }
    if (finishReason === 'COMPLETED') scheduleCompletionReconcile(bridge)
    // §3.5-C：计划分批确认终态（BATCH_WAITING，同计划仍有待确认卡）后，重新同步待办队列，
    // 确保前端能"看到"同计划下一张待确认卡。仅在此终态触发——避免每次 run_done 都重置提问卡
    // 进度。前端先按 session 清空再入队，且 enqueueUserAction 按 pendingId 去重，无重复卡风险
    // （断连重连场景也受益，验收 10）。
    if (finishReason === 'BATCH_WAITING') {
      void resyncPendingActionsAfterBatchWait(bridge)
    }
    // 继续回合状态（所有回合共用 reducer）：异常停止（步数上限/预算熔断）置位提示条，
    // 正常完成/挂起等待清除。2026-09-23 会话 ai-4f4fb3d4 证明：只在 continueRun 收尾判定
    // 会漏掉首次触发（sendMessage 回合）——必须在 reducer 统一处理。
    bridge.resumableStop.value = isResumableStopReason(finishReason)
      ? { reason: finishReason as ResumableStopReason }
      : null
    abortCtrl.abort()
    return
  }

  if (eventName === 'run_error') {
    acc.streamErrored = true
    const message = formatSseStreamError(
      typeof data.message === 'string' ? data.message : 'AI 助手执行失败'
    )
    if (isViewingOwner(bridge)) {
      lastError.value = message
    }
    sessionsStore.abandonSessionTitleGenerating(ownerSessionIdOf(bridge) || sessionId.value)
    // 异常中断同样允许继续：后端 error 终态的 phase='error' 可继续（与 run_done 判定同源）。
    bridge.resumableStop.value = { reason: 'ERROR' }
    const previous = currentAssistant()
    patchPending({
      content: previous?.content?.trim() ? previous.content : '抱歉，AI 助手暂时无法响应。',
      pending: false,
      awaitingNext: false,
      compressing: false,
      error: message,
      importProgress: null
    })
    abortCtrl.abort()
    return
  }

  pushAiDebug('stream', 'Ignored unknown SSE v2 event', { eventName })
}

export type { AiTimelineSegment }
